import { RenderStoryOptions, SmartPillArbitrator, SmartPillData } from 'owa-smart-pill';
import { FeatureNames } from 'owa-mail-smart-pill-features';
import isPillTypeEnabled from 'owa-mail-smart-pill-features/lib/utils/isPillTypeEnabled';
import isConfidenceScoreAcceptable from 'owa-mail-smart-pill-features/lib/utils/isConfidenceScoreAcceptable';
import getStrategiesForRenderingSmartPills, {
    SmartPillRenderingStrategy,
    SmartPillRenderingStrategies,
} from '../utils/getStrategiesForRenderingSmartPills';
import getSmartPillsRenderingStrategy from '../strategies/getSmartPillsRenderingStrategy';
import SmartPillsRenderingStrategy from '../strategies/SmartPillsRenderingStrategy';
import { isFeatureEnabled } from 'owa-feature-flags';

const ARBITRATOR_ID: string = 'complexArbitrator';

/**
 * Arbitrator that can handle smart doc, smart time, and smart reply pills
 * Priority is as follows:
 * 1. Smart doc (not mixed with other pill types)
 * 2. Smart time (by itself or mixed with smart reply)
 * 3. Smart reply
 */
export const complexArbitrator: SmartPillArbitrator = {
    arbitratePills: (pills: SmartPillData[]) => {
        const pillsToShow: SmartPillData[] = [];
        const pillsToHide: SmartPillData[] = [];

        if (pills) {
            // First: Filter pills by feature.
            const pillsFilteredByFeature: SmartPillData[] = filterPills(
                pills,
                pillsToHide,
                isPillTypeEnabled,
                RenderStoryOptions.FeatureIsNotEnabled
            );

            // Second: Filter pills by confidence score.
            const pillsFullyFiltered: SmartPillData[] = filterPills(
                pillsFilteredByFeature,
                pillsToHide,
                isConfidenceScoreAcceptable,
                RenderStoryOptions.ConfidenceLevelTooLow
            );

            // Now pick which pills to show.
            if (pillsFullyFiltered.length > 0) {
                // Enables smart rendering strategies V2
                if (isFeatureEnabled('mc-smartPillsRenderingStrategiesV2')) {
                    // Get a strategy
                    const renderingStrategy: SmartPillsRenderingStrategy = getSmartPillsRenderingStrategy();

                    // End filtering by selecting pills based on strategy
                    pillsToShow.push(...renderingStrategy.selectPills(pillsFullyFiltered));
                } else {
                    // Get strategy on how to show renderable pills.
                    const renderingStrategies: SmartPillRenderingStrategies = getStrategiesForRenderingSmartPills(
                        pillsFullyFiltered
                    );

                    if (renderingStrategies) {
                        // Ordering here matters higher priority from top (Todo) to bottom (Time)
                        const smartRenderingStrategyMap = new Map<
                            string,
                            SmartPillRenderingStrategy
                        >([
                            [FeatureNames.SmartReply, renderingStrategies.smartReply],
                            [FeatureNames.SmartDoc, renderingStrategies.smartDoc],
                            [FeatureNames.SmartTime, renderingStrategies.smartTime],
                            [FeatureNames.SmartTodo, renderingStrategies.smartTodo],
                            [FeatureNames.SmartReminder, renderingStrategies.smartReminder],
                        ]);

                        smartRenderingStrategyMap.forEach(
                            (renderStrategy: SmartPillRenderingStrategy, featureName: string) => {
                                if (renderStrategy) {
                                    pillsToShow.push(
                                        ...getPillsBasedOnStrategy(
                                            featureName,
                                            renderStrategy,
                                            pillsFullyFiltered
                                        )
                                    );
                                }
                            }
                        );
                    }
                }

                // Populate pillsToHide with any pills not included in pillsToShow.
                for (let i = 0; i < pillsFullyFiltered.length; i++) {
                    const filteredPill: SmartPillData = pillsFullyFiltered[i];
                    if (!pillsToShow.some(pillToShow => pillToShow === filteredPill)) {
                        filteredPill.logData.mainPayload.renderStory |=
                            RenderStoryOptions.RejectedByArbitrator;
                        pillsToHide.push(filteredPill);
                    }
                }
            }
        }

        return {
            pillsToShow: pillsToShow,
            pillsToHide: pillsToHide,
            arbitratorId: ARBITRATOR_ID,
        };
    },
};

/**
 * @param pillFeatureName: The feature name of the pill
 * @param smartPillStrategy: Strategy to get pills
 * @param enabledPills: Pills to apply strategy to
 */
function getPillsBasedOnStrategy(
    pillFeatureName: string,
    smartPillStrategy: SmartPillRenderingStrategy,
    enabledPills: SmartPillData[]
): SmartPillData[] {
    return enabledPills
        .filter(pill => pill.featureName === pillFeatureName)
        .slice(0, smartPillStrategy.numOfPills);
}

/**
 * @param pills: Array of pills to filter on
 * @param pillsToHide: Array to place filtered-out pills into
 * @param filterFunction: Function used to filter pills
 * @param renderStoryOption: RenderStoryOption for filtered-out pills
 */
function filterPills(
    pills: SmartPillData[],
    pillsToHide: SmartPillData[],
    filterFunction: (pill: SmartPillData) => boolean,
    renderStoryOption: RenderStoryOptions
): SmartPillData[] {
    const filteredPills: SmartPillData[] = [];
    pills.forEach(pill => {
        if (filterFunction(pill)) {
            filteredPills.push(pill);
        } else {
            pill.logData.mainPayload.renderStory |= renderStoryOption;
            pillsToHide.push(pill);
        }
    });

    return filteredPills;
}

export default complexArbitrator;
